import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { useT } from '../lib/i18n';
import '../styles/drawer.css';

interface DrawerProps {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
}

/**
 * Painel lateral para abrir um REGISTRO a partir de uma lista.
 *
 * Existe separado do `Modal` porque a intencao e outra. Modal interrompe: "responda
 * isto para continuar". Painel acompanha: a lista continua visivel atras, e fechar
 * devolve a pessoa exatamente ao lugar de onde saiu. Para ficha de paciente aberta de
 * uma tabela, a segunda leitura e a certa — ela vai abrir varias em sequencia.
 *
 * Continua sobre `<dialog>` nativo pelo mesmo motivo do Modal: foco preso, `Esc` e
 * anuncio para leitor de tela vem de graca e corretos.
 */
export function Drawer({ title, subtitle, onClose, children, footer }: DrawerProps): ReactElement {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null || dialog.open) return;
    dialog.showModal();
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  return createPortal(
    <dialog
      ref={ref}
      className="drawer"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="drawer__head">
        <div className="drawer__id">
          <h2 className="drawer__title">{title}</h2>
          {subtitle === undefined ? null : <div className="drawer__subtitle">{subtitle}</div>}
        </div>
        <button
          type="button"
          className="btn btn--icon"
          onClick={onClose}
          aria-label={t.common.close}
        >
          <Icon name="x" size={17} />
        </button>
      </div>

      <div className="drawer__body">{children}</div>

      {footer === undefined ? null : <div className="drawer__foot">{footer}</div>}
    </dialog>,
    document.body,
  );
}
